<template>
  <div class="dropdown-button" :data-type="type">
    <button
      role="menuitem"
      aria-haspopup="true"
      class="icon-button expand-button"
      @click.prevent.stop="show"
      v-t:title="'Padlet actions'"
    >
      <base-icon name="more_vert" />
    </button>
    <div v-if="showDropdown" class="dropdown-menu">
      <div class="dropdown dropdown-with-curtain" @click.self.prevent.stop="cancel">
        <menu class="dropdown-content">
          <div class="tv tv-list">
            <a
              v-if="activeFolder && usableFolders.length > 0"
              class="tv-cell tv-cell-default is-actionable"
              data-app-target="none"
              @click.prevent.stop="moveToFolder"
            >
              <base-icon name="send" /><span v-t>Move to folder</span>
            </a>
            <a
              v-if="usableFolders.length > 0"
              class="tv-cell tv-cell-default is-actionable"
              data-app-target="none"
              @click.prevent.stop="addToFolder"
            >
              <base-icon name="copy" /><span v-t>Add to folder</span>
            </a>
            <a
              v-if="activeFolder"
              class="tv-cell tv-cell-default is-actionable"
              data-app-target="none"
              @click.prevent.stop="removeFromFolder"
            >
              <base-icon name="close" /><span v-t>Remove from folder</span>
            </a>
            <a class="tv-cell tv-cell-default is-actionable" @click.prevent="copyLink">
              <base-icon name="link" /><span v-t>Copy link</span>
            </a>
            <a v-if="canArchive" class="tv-cell tv-cell-default is-actionable" @click.prevent="archiveWall">
              <base-icon name="archive" /><span v-t>Archive</span>
            </a>
            <a v-if="canUnarchive" class="tv-cell tv-cell-default is-actionable" @click.prevent="unarchiveWall">
              <base-icon name="archive" /><span v-t>Unarchive</span>
            </a>
            <div v-if="showDivider" class="tv-divider" />
            <a
              v-if="canDelete"
              class="tv-cell tv-cell-default is-actionable"
              data-app-target="none"
              @click.prevent.stop="confirmDeleteWall"
            >
              <base-icon name="delete" /><span v-t>Delete padlet</span>
            </a>
          </div>
        </menu>
      </div>
    </div>
    <div v-if="showFolderSelector" class="modal standard-scheme" @click.self.prevent.stop="close">
      <div class="modal-container">
        <header v-t>Choose a folder</header>
        <folder-selector
          class="list modal-list"
          :folders="usableFolders"
          @select="folderSelected"
          @cancel.stop="cancel"
        />
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapState, mapGetters, mapActions } from 'vuex'
import BaseIcon from 'vuecomponents/base_icon'
import FolderSelector from 'vuecomponents/folder_selector'
import EscapeToCancelMixin from 'vuemixins/escape_to_cancel_mixin'
import IntlMixin from 'vuemixins/intl_mixin'
import { folderForSelector } from 'bits/folder'
import { __ } from 'bits/intl'
import { deleteWallConfirmationParams } from 'bits/confirmation_dialog'
import { copyToClipboard } from 'bits/clipboard'
import { currentHostWithProtocol } from 'bits/location'

export default {
  components: { BaseIcon, FolderSelector },
  mixins: [EscapeToCancelMixin, IntlMixin],
  props: {
    wall: {
      default: {},
    },
    type: {
      default: 'card',
    },
  },
  data() {
    return {
      state: null,
      showDropdown: false,
      showFolderSelector: false,
      foldersFetched: false,
    }
  },
  watch: {
    showDropdown(value) {
      if (value && !this.foldersFetched) {
        this.$store.dispatch('fetchWallFolders', this.wall)
        this.foldersFetched = true
      }
    },
  },
  computed: {
    ...mapState(['user', 'folders']),
    ...mapGetters(['activeFolder', 'isWallInFolder']),
    usableFolders() {
      let usable = this.folders
      if (this.activeFolder) {
        usable = this.folders.filter((f) => f.id !== this.activeFolder.id)
      }
      return usable.map((f) => {
        const disabled = this.isWallInFolder(this.wall.id, f.id)
        const message = disabled ? this.__('Already in folder') : ''
        return folderForSelector(f, { disabled, message })
      })
    },
    canDelete() {
      return this.wall.builder.id === this.user.id
    },
    canArchive() {
      return this.wall.builder.id === this.user.id && !this.wall.archived_at
    },
    canUnarchive() {
      return this.wall.builder.id === this.user.id && this.wall.archived_at
    },
    showDivider() {
      return this.activeFolder && this.canDelete
    },
  },
  methods: {
    ...mapActions('confirmationDialog', ['openConfirmationDialog']),
    show() {
      this.showDropdown = true
    },
    close() {
      this.state = null
      this.showFolderSelector = false
      this.showDropdown = false
    },
    cancel() {
      this.close()
    },
    moveToFolder() {
      this.state = 'moving'
      this.showFolderSelector = true
    },

    addToFolder() {
      this.state = 'adding'
      this.showFolderSelector = true
    },

    removeFromFolder() {
      this.state = 'removing'
      this.$store
        .dispatch('removeFromFolder', {
          wallId: this.wall.id,
          folderId: this.activeFolder.id,
        })
        .then(() => {
          this.state = null
        })
    },

    folderSelected(selectedFolder) {
      switch (this.state) {
        case 'moving':
          this.$store
            .dispatch('moveToFolder', {
              toFolderId: selectedFolder.id,
              fromFolderId: this.activeFolder.id,
              wallId: this.wall.id,
            })
            .then(() => {
              this.showFolderSelector = false
              this.showDropdown = false
            })
          break
        case 'adding':
          this.$store
            .dispatch('addToFolder', {
              wallId: this.wall.id,
              folderId: selectedFolder.id,
            })
            .then(() => {
              this.showFolderSelector = false
              this.showDropdown = false
            })
          break
        default:
          break
      }
      this.state = null
    },

    archiveWall() {
      this.showDropdown = false
      this.$store.dispatch('archiveWall', { wallId: this.wall.id })
    },

    unarchiveWall() {
      this.showDropdown = false
      this.$store.dispatch('unarchiveWall', { wallId: this.wall.id })
    },

    confirmDeleteWall() {
      this.openConfirmationDialog({
        ...deleteWallConfirmationParams(this.wall.title),
        afterConfirmActions: [{ type: 'deleteWall', payload: { wallId: this.wall.id } }],
      })
      this.showDropdown = false
    },

    copyLink() {
      const wallAddress = `${currentHostWithProtocol()}/${this.wall.address}`
      copyToClipboard(wallAddress)
      this.showDropdown = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
[data-type='list'] .expand-button {
  padding: 8px 4px 8px 12px;
}
[data-type='list'] .expand-button i {
  color: var(--muted-text-color);
  @include directional-margin($margin-right: 0, $margin-left: 0);
}
</style>
